import {
  selectIpxEstimatedRegionBytes,
  selectIpxOrderMode,
  selectIpxRegionVolume,
  selectIpxSentScanCount,
} from "@/store/modes/ipx-order-mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { useDashboardProjectUrl } from "@/utils/redirects";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import { selectProjectId, selectProjectName } from "@faro-lotv/project-source";
import { Link, Stack } from "@mui/material";
import type { JSX } from "react";
import { FloorAreaPanel } from "../floor-area/floor-area-panel";
import { SummaryList, SummaryRow } from "./order-placed-step";
import {
  formatDataSize,
  formatFloorArea,
  formatVolume,
  PENDING_VALUE,
} from "../ipx-measures-format";

/** @returns the summary of what is sent to IPX, whose terms are accepted in the IPX checkout */
export function ReviewPanel(): JSX.Element {
  const { regionMeasures } = useAppSelector(selectIpxOrderMode);
  const volume = useAppSelector(selectIpxRegionVolume);
  const bytes = useAppSelector(selectIpxEstimatedRegionBytes);
  const scanCount = useAppSelector(selectIpxSentScanCount);
  const projectName = useAppSelector(selectProjectName);
  const projectUrl = useDashboardProjectUrl(useAppSelector(selectProjectId));

  return (
    <Stack sx={{ gap: 3, flexGrow: 1 }}>
      <SummaryList>
        {projectName && (
          <SummaryRow label="Project">
            {projectUrl ? (
              // A new tab, so the order in progress is kept
              <Link href={projectUrl} target="_blank" rel="noopener noreferrer">
                {projectName}
              </Link>
            ) : (
              projectName
            )}
          </SummaryRow>
        )}
        {scanCount !== undefined && (
          <SummaryRow label="Scans">{scanCount.toLocaleString()}</SummaryRow>
        )}
        <SummaryRow label="Size">
          {regionMeasures && bytes !== undefined
            ? formatDataSize(bytes, regionMeasures.export?.points)
            : PENDING_VALUE}
        </SummaryRow>
        <SummaryRow label="Volume">
          {volume === undefined ? PENDING_VALUE : formatVolume(volume)}
        </SummaryRow>
        <SummaryRow label="Area">
          {regionMeasures
            ? formatFloorArea(regionMeasures.planAreaM2)
            : PENDING_VALUE}
        </SummaryRow>
      </SummaryList>
      <FloorAreaPanel />
      <FaroText variant="bodyS" color={grey[600]} sx={{ mt: "auto" }}>
        IPX is a third-party service: it handles the order, its terms, the
        payment and the model. FARO support helps with Sphere XG.
      </FaroText>
    </Stack>
  );
}
